import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import IntegrationErrorBoundary from "@/components/common/ErrorBoundary";
import BackfillProgressCard from "@/components/common/BackfillProgressCard";
import { useAccount } from "@/contexts/account.context";
import { useIntegrationContext } from "@/contexts/IntegrationContext";
import { useAuth } from "@/contexts/auth.context";
import {
  deleteUserConnectionApiV1UsersMeConnectionsDelete,
  getIntegrationAuthUrlApiV1IntegrationsNexusAuthUrlGet,
  getUserConnectionsApiV1UsersMeConnectionsGet,
} from "@/backend-sdk/client/sdk.gen";
import { isHeyApiError } from "@/lib/hey-api-client";
import { useAccountIntegrationsData } from "@/hooks/useAccountIntegrationsData";
import { INTEGRATIONS_WELCOME_PENDING_USER_ID_KEY } from "@/lib/onboarding";
import { useToast } from "@/hooks/use-toast";
import {
  AlertTriangle,
  ChevronRight,
  Clock,
  Loader2,
  Sparkles,
} from "lucide-react";

// Lista de segmentos de e-commerce
const ECOMMERCE_SEGMENTS = [
  "Artesanato e personalizados",
  "Beleza e cosmeticos",
  "Calcados",
  "Casa e decoracao",
  "Infantil e bebe",
  "Joias e semijoias",
  "Moda",
  "Moda feminina",
  "Moda fitness",
  "Moda infantil",
  "Moda masculina",
  "Outro",
  "Perfumaria",
  "Presentes",
  "Religiao e artigos religiosos",
];

// Ícone oficial do Google
const GoogleIcon = ({ className }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

// Ícone oficial do Meta (imagem com "zoom" para remover espaços em branco)
const MetaIcon = ({ className }: { className?: string }) => (
  <span
    className={[
      "inline-flex items-center justify-center overflow-hidden",
      className,
    ]
      .filter(Boolean)
      .join(" ")}
  >
    <img
      src="/metaLogo.png"
      alt="Meta"
      className="block h-full w-full object-contain"
      style={{ transform: "scale(1.45)" }}
    />
  </span>
);

interface IntegrationCardConfig {
  id: "google" | "meta";
  name: string;
  description: string;
  icon: React.FC<{ className?: string }>;
  color: string;
  highlights: string[];
}

const CARDS: IntegrationCardConfig[] = [
  {
    id: "google",
    name: "Google",
    description:
      "Conecta Google Analytics, Google Ads e Search Console com um único fluxo OAuth.",
    icon: GoogleIcon,
    color: "bg-white border border-gray-200",
    highlights: ["Google Analytics", "Google Ads", "Search Console"],
  },
  {
    id: "meta",
    name: "Meta",
    description:
      "Conecta Meta Ads e Instagram Insights com um único fluxo OAuth.",
    icon: MetaIcon,
    color: "bg-white border border-gray-200",
    highlights: ["Meta Ads", "Instagram Insights"],
  },
];

const BRAZIL_TIMEZONE = "America/Sao_Paulo";
const BRAZIL_TIMEZONE_LABEL = "GMT-03:00";

const CARD_INTEGRATION_LAST_SYNC: Record<
  IntegrationCardConfig["id"],
  Array<{ integration: string; label: string }>
> = {
  google: [
    { integration: "google_analytics", label: "Google Analytics" },
    { integration: "google_ads", label: "Google Ads" },
    { integration: "search_console", label: "Search Console" },
  ],
  meta: [
    { integration: "meta_ads", label: "Meta Ads" },
    { integration: "instagram_insights", label: "Instagram Insights" },
    { integration: "facebook_insights", label: "Facebook Insights" },
  ],
};

function formatBrazilSyncDate(dateValue: string): string {
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "-";

  const formatted = new Intl.DateTimeFormat("pt-BR", {
    timeZone: BRAZIL_TIMEZONE,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  })
    .format(date)
    .replace(",", "");

  return `${formatted} ${BRAZIL_TIMEZONE_LABEL}`;
}

const IntegrationsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const { refreshGlobalHealth } = useIntegrationContext();
  const { selectedAccount } = useAccount();

  const {
    connectionStatus,
    tokenIssues,
    syncingBySource,
    pendingJobsBySource,
    lastSyncByIntegration,
    syncStatus,
    isPending,
    refetch,
  } = useAccountIntegrationsData({ refetchInterval: 10000 });

  const dataPeriodByIntegration = syncStatus?.data_period_by_integration;

  const [loading, setLoading] = useState<Record<"google" | "meta", boolean>>({
    google: false,
    meta: false,
  });
  const [welcomeModalOpen, setWelcomeModalOpen] = useState(false);
  const [disconnectModalOpen, setDisconnectModalOpen] = useState(false);
  const [selectedCardForDisconnect, setSelectedCardForDisconnect] =
    useState<IntegrationCardConfig | null>(null);

  // Verificar se o usuário já completou o onboarding (selecionou segmento) POR CONTA
  const activeAccountId = selectedAccount?.id || "";
  const getStoredSegmentForAccount = useCallback((accountId: string) => {
    if (accountId)
      return localStorage.getItem(`user_ecommerce_segment_${accountId}`);
    return localStorage.getItem("user_ecommerce_segment");
  }, []);
  const [selectedSegment, setSelectedSegment] = useState<string>(
    getStoredSegmentForAccount(activeAccountId) || "",
  );
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState<boolean>(
    !!getStoredSegmentForAccount(activeAccountId),
  );

  // Verificar se há alguma conexão ativa usando estado local
  const hasConnection = useMemo(
    () => Object.values(connectionStatus).some((s) => s === "connected"),
    [connectionStatus],
  );
  const shouldShowWelcomeOnboarding = useMemo(() => {
    if (!user?.id) return false;
    const pendingWelcomeUserId = localStorage.getItem(
      INTEGRATIONS_WELCOME_PENDING_USER_ID_KEY,
    );
    return pendingWelcomeUserId === user.id;
  }, [user]);

  const sleep = (ms: number) =>
    new Promise((resolve) => setTimeout(resolve, ms));

  // Refresh global health once initial data loads
  useEffect(() => {
    if (!isPending) {
      refreshGlobalHealth();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPending]);

  // Resetar estado do onboarding quando a conta ativa muda
  useEffect(() => {
    const storedSegment = getStoredSegmentForAccount(activeAccountId);
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedSegment(storedSegment || "");
    setHasCompletedOnboarding(!!storedSegment);
  }, [activeAccountId, getStoredSegmentForAccount]);

  // Handle OAuth callback
  useEffect(() => {
    let timeoutId: number | undefined;

    const params = new URLSearchParams(window.location.search);
    const integrationResult = params.get("integration");
    const sourceResult = params.get("source");
    const name = params.get("name") || "Integração";

    if (integrationResult === "success") {
      toast({
        title: `${name} conectada!`,
        description: "Os dados estão sendo sincronizados.",
      });
      timeoutId = window.setTimeout(() => {
        refetch();
      }, 2000);
    }

    if (sourceResult) {
      timeoutId = window.setTimeout(() => {
        refetch();
      }, 1000);
    }

    if (integrationResult === "error") {
      const message = params.get("message") || "Erro desconhecido";
      toast({
        title: "Erro na conexão",
        description: `Não foi possível conectar ${name}: ${message}`,
        variant: "destructive",
      });
    }

    if (integrationResult || sourceResult) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isPending) return;

    const shouldOpen = !hasConnection && shouldShowWelcomeOnboarding;
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setWelcomeModalOpen(shouldOpen);

    if (shouldOpen) {
      localStorage.removeItem(INTEGRATIONS_WELCOME_PENDING_USER_ID_KEY);
    }
  }, [hasConnection, isPending, shouldShowWelcomeOnboarding]);

  const getCardLastSyncEntries = useCallback(
    (cardId: IntegrationCardConfig["id"]) =>
      CARD_INTEGRATION_LAST_SYNC[cardId].flatMap((entry) => {
        const syncInfo = lastSyncByIntegration?.[entry.integration];
        return syncInfo ? [{ ...entry, syncInfo }] : [];
      }),
    [lastSyncByIntegration],
  );

  const handleCompleteOnboarding = useCallback(() => {
    if (selectedSegment) {
      if (activeAccountId) {
        localStorage.setItem(
          `user_ecommerce_segment_${activeAccountId}`,
          selectedSegment,
        );
      }
      localStorage.setItem("user_ecommerce_segment", selectedSegment);
      setHasCompletedOnboarding(true);
    }
    setWelcomeModalOpen(false);
  }, [activeAccountId, selectedSegment]);

  const handleConnect = async (card: IntegrationCardConfig) => {
    try {
      setLoading((prev) => ({ ...prev, [card.id]: true }));
      const alias = `${card.id}-default`;
      const { data } =
        await getIntegrationAuthUrlApiV1IntegrationsNexusAuthUrlGet({
          throwOnError: true,
          query: { source: card.id, alias },
        });
      toast({
        title: "Redirecionando...",
        description: `Autorize a conexão com ${card.name}.`,
      });
      // eslint-disable-next-line react-hooks/immutability
      window.location.href = data.url;
    } catch {
      toast({
        title: "Erro",
        description: `Não foi possível conectar ${card.name}.`,
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({ ...prev, [card.id]: false }));
    }
  };

  const handleDisconnect = (card: IntegrationCardConfig) => {
    setSelectedCardForDisconnect(card);
    setDisconnectModalOpen(true);
  };

  const handleConfirmDisconnect = async () => {
    if (!selectedCardForDisconnect) return;

    try {
      setLoading((prev) => ({ ...prev, [selectedCardForDisconnect.id]: true }));

      const customerUuid = localStorage.getItem("active_customer_uuid");

      if (!customerUuid) {
        throw new Error(
          "Customer UUID não encontrado para desconectar integração.",
        );
      }

      await deleteUserConnectionApiV1UsersMeConnectionsDelete({
        throwOnError: true,
        query: {
          source: selectedCardForDisconnect.id,
          alias: `${selectedCardForDisconnect.id}-default`,
          customer_uuid: customerUuid,
        },
      });

      toast({
        title: `${selectedCardForDisconnect.name} desconectado`,
        description:
          "A conexão e todos os dados relacionados foram removidos com sucesso.",
      });
      window.location.reload();
    } catch (error: unknown) {
      console.error("Erro ao desconectar:", error);

      const message = error instanceof Error ? error.message.toLowerCase() : "";
      const statusCode = isHeyApiError(error) ? error.status : undefined;
      const isTimeout = message.includes("timeout");
      const isRetryable = statusCode === 503 || statusCode === 504 || isTimeout;

      if (isRetryable) {
        toast({
          title: "Confirmando desconexão...",
          description:
            "A exclusão pode levar alguns segundos. Vamos checar o status da conexão e finalizar automaticamente.",
        });

        const customerUuid = localStorage.getItem("active_customer_uuid");
        const source = selectedCardForDisconnect.id;

        const maxAttempts = 12;
        for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
          try {
            await sleep(2000);
            if (!customerUuid) {
              throw new Error(
                "Customer UUID não encontrado para confirmar desconexão.",
              );
            }

            const { data: payload } =
              await getUserConnectionsApiV1UsersMeConnectionsGet({
                throwOnError: true,
                query: { customer_uuid: customerUuid },
              });
            const conn = payload.connections.find((c) => c.source === source);
            if (!conn || conn.connected === false) {
              toast({
                title: `${selectedCardForDisconnect.name} desconectado`,
                description: "A conexão foi removida com sucesso.",
              });
              window.location.reload();
              return;
            }
          } catch {
            // ignorar e continuar tentando
          }
        }
      }

      toast({
        title: "Erro ao desconectar",
        description: `Não foi possível desconectar ${selectedCardForDisconnect.name}.`,
        variant: "destructive",
      });
    } finally {
      setLoading((prev) => ({
        ...prev,
        [selectedCardForDisconnect.id]: false,
      }));
      setDisconnectModalOpen(false);
      setSelectedCardForDisconnect(null);
    }
  };

  return (
    <IntegrationErrorBoundary
      integrationType="general"
      integrationId="integrations-page"
    >
      <div className="p-6 space-y-6">
        <Dialog open={welcomeModalOpen} onOpenChange={setWelcomeModalOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Sparkles className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <DialogTitle className="text-xl">
                    Bem-vindo ao Painel do E-Commerce!
                  </DialogTitle>
                </div>
              </div>
            </DialogHeader>
            <DialogDescription className="text-base pt-2">
              Conecte sua conta Google ou Meta para iniciar as sincronizações.
            </DialogDescription>

            <div className="space-y-3 pt-2">
              <Label htmlFor="segment" className="text-sm font-medium">
                Selecione o segmento de atuação do seu e-commerce:
              </Label>
              <Select
                value={selectedSegment}
                onValueChange={setSelectedSegment}
              >
                <SelectTrigger id="segment" className="w-full">
                  <SelectValue placeholder="Escolha um segmento..." />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {ECOMMERCE_SEGMENTS.map((segment) => (
                    <SelectItem key={segment} value={segment}>
                      {segment}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter className="flex gap-2 pt-4">
              <Button variant="outline" onClick={handleCompleteOnboarding}>
                Fechar
              </Button>
              <Button
                onClick={handleCompleteOnboarding}
                className="bg-primary hover:bg-primary/90"
                disabled={!selectedSegment}
              >
                Começar <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog
          open={disconnectModalOpen}
          onOpenChange={setDisconnectModalOpen}
        >
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-destructive/10">
                  <AlertTriangle className="h-6 w-6 text-destructive" />
                </div>
                <div>
                  <DialogTitle className="text-xl">
                    Confirmar Desconexão
                  </DialogTitle>
                </div>
              </div>
            </DialogHeader>
            <DialogDescription className="text-base pt-2">
              Tem certeza de que deseja desconectar{" "}
              <strong>{selectedCardForDisconnect?.name}</strong>?
              <br />
              <br />
              <span className="text-destructive">
                Após a desconexão, o sistema não sincronizará mais os dados das
                contas do usuário.
              </span>
              <br />
              <br />
              Esta ação não pode ser desfeita automaticamente.
            </DialogDescription>
            <DialogFooter className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => setDisconnectModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="destructive"
                onClick={handleConfirmDisconnect}
                disabled={loading[selectedCardForDisconnect?.id || "google"]}
              >
                {loading[selectedCardForDisconnect?.id || "google"]
                  ? "Desconectando..."
                  : "Confirmar Desconexão"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Integrações</h1>
            <p className="text-muted-foreground">
              Conecte suas plataformas de marketing e acompanhe tudo em um só
              lugar.
            </p>
          </div>
        </header>

        {/* Token health warnings */}
        {(["google", "meta"] as const).map((source) => {
          const issue = tokenIssues[source];
          if (!issue) return null;
          const cardConfig = CARDS.find((c) => c.id === source);
          return (
            <Card
              key={`token-warning-${source}`}
              className="max-w-5xl mx-auto border-destructive/50 bg-destructive/5"
            >
              <CardContent className="flex items-start gap-4 p-4">
                <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-destructive">
                    {cardConfig?.name ?? source} precisa de atenção
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {issue.last_error
                      ? `Token invalidado pelo provedor: ${issue.last_error}`
                      : `O token de conexão com ${cardConfig?.name ?? source} foi invalidado. Reconecte para continuar sincronizando os dados.`}
                  </p>
                </div>
                <Button
                  size="sm"
                  disabled={loading[source]}
                  onClick={() => cardConfig && handleConnect(cardConfig)}
                >
                  {loading[source] ? "Conectando..." : "Reconectar"}
                </Button>
              </CardContent>
            </Card>
          );
        })}

        <section className="grid gap-6 grid-cols-12 max-w-5xl mx-auto">
          {CARDS.map((card) => {
            const Icon = card.icon;
            const isConnected = connectionStatus[card.id] === "connected";
            const isSyncing = syncingBySource[card.id];
            const pendingJobs = pendingJobsBySource[card.id];
            const integrationLastSyncs = getCardLastSyncEntries(card.id);

            const needsAccountSelection =
              isConnected &&
              !isSyncing &&
              integrationLastSyncs.length === 0 &&
              pendingJobs === 0;

            return (
              <Card
                key={card.id}
                className="col-span-12 md:col-span-6 p-6 space-y-4"
              >
                <div className="flex items-start gap-4">
                  <span className={`p-3 rounded-xl ${card.color}`}>
                    <Icon className="h-8 w-8" />
                  </span>
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h2 className="text-xl font-semibold">{card.name}</h2>
                      {isPending ? (
                        <Badge variant="outline" className="gap-1">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Verificando...
                        </Badge>
                      ) : isSyncing ? (
                        <Badge
                          variant="outline"
                          className="bg-blue-500/10 text-blue-500 border-blue-500/30"
                        >
                          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                          Sincronizando
                        </Badge>
                      ) : needsAccountSelection ? (
                        <Badge
                          variant="outline"
                          className="bg-amber-500/10 text-amber-600 border-amber-500/30"
                        >
                          <Clock className="h-3 w-3 mr-1" />
                          Aguardando contas
                        </Badge>
                      ) : (
                        <Badge
                          variant={isConnected ? "default" : "outline"}
                          className={isConnected ? "bg-green-600" : ""}
                        >
                          {isConnected ? "Conectado" : "Desconectado"}
                        </Badge>
                      )}
                    </div>
                    <p className="text-muted-foreground text-sm">
                      {card.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Produtos incluídos: {card.highlights.join(", ")}
                    </p>
                    {isPending ? (
                      <div className="h-16 animate-pulse bg-muted rounded" />
                    ) : (
                      <div className="text-xs text-muted-foreground space-y-1">
                        {isSyncing && pendingJobs > 0 && (
                          <p className="text-blue-500 flex items-center gap-1">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {pendingJobs} job{pendingJobs > 1 ? "s" : ""} na
                            fila
                          </p>
                        )}
                        {integrationLastSyncs.length > 0 ? (
                          <div className="flex flex-col gap-1">
                            {integrationLastSyncs.map(
                              ({ integration, label, syncInfo }) => {
                                const totalDays =
                                  dataPeriodByIntegration?.[integration]
                                    ?.total_days;
                                return (
                                  <Badge
                                    key={integration}
                                    variant="outline"
                                    className="bg-emerald-500/10 text-emerald-600 border-emerald-500/30 font-normal gap-1 justify-start"
                                  >
                                    <Clock className="h-3 w-3 shrink-0" />
                                    <span>
                                      {label}:{" "}
                                      {formatBrazilSyncDate(
                                        syncInfo.last_completed_at,
                                      )}
                                      {totalDays != null && (
                                        <span className="text-emerald-500">
                                          {" "}
                                          ({totalDays} dia
                                          {totalDays > 1 ? "s" : ""}{" "}
                                          sincronizado
                                          {totalDays > 1 ? "s" : ""})
                                        </span>
                                      )}
                                    </span>
                                  </Badge>
                                );
                              },
                            )}
                            <p className="text-[11px] text-muted-foreground">
                              Horário de Brasília ({BRAZIL_TIMEZONE_LABEL})
                            </p>
                          </div>
                        ) : needsAccountSelection ? (
                          <p className="text-amber-600">
                            Conecte suas contas para iniciar a sincronização
                          </p>
                        ) : isConnected && !isSyncing ? (
                          <Badge
                            variant="outline"
                            className="bg-amber-500/10 text-amber-600 border-amber-500/30 font-normal gap-1"
                          >
                            <Clock className="h-3 w-3" />
                            Aguardando primeira sincronização
                          </Badge>
                        ) : !isConnected ? (
                          <p>Conecte para iniciar a sincronização</p>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid gap-2">
                  {isPending ? (
                    <Button size="sm" className="w-full" disabled>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Carregando...
                    </Button>
                  ) : needsAccountSelection ? (
                    <>
                      <Button
                        size="sm"
                        className="w-full"
                        onClick={() =>
                          navigate(
                            `/dashboard/account-selection?source=${card.id}`,
                          )
                        }
                      >
                        Selecionar contas
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={loading[card.id]}
                        onClick={() => handleDisconnect(card)}
                      >
                        Desconectar
                      </Button>
                    </>
                  ) : isConnected ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        disabled={loading[card.id]}
                        onClick={() => handleDisconnect(card)}
                      >
                        Desconectar
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="sm"
                      className="w-full"
                      disabled={loading[card.id]}
                      onClick={() => handleConnect(card)}
                    >
                      {loading[card.id] ? "Conectando..." : "Conectar"}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}

          {/* Card de progresso do backfill - exibido quando há conexões ativas */}
          {hasConnection && <BackfillProgressCard />}
        </section>

        <Card className="bg-muted/50">
          <div className="p-6 text-center space-y-4">
            <h3 className="text-lg font-semibold">Precisa de ajuda?</h3>
            <p className="text-muted-foreground">
              Fale com o suporte para configurar suas integrações.
            </p>
            <div className="flex gap-3 justify-center">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  window.open(
                    "https://api.whatsapp.com/send?phone=5551980281805&text=Preciso%20de%20suporte",
                    "_blank",
                  )
                }
              >
                Contatar suporte
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </IntegrationErrorBoundary>
  );
};

export default IntegrationsPage;
